/* Before & After.
 *
 * Drives the case selector on the `before-and-after` section: the tab strip,
 * the prev / next chevrons and which pair is on screen.
 *
 * The section renders every case and hides all but the first, so with this
 * file absent the first pair still shows and nothing is broken. Only the
 * switching needs script.
 *
 * The section emits its own <script src> tag, so a page with two of these asks
 * for the file twice. The browser dedupes the fetch; this guard dedupes the
 * execution.
 */
(function () {
  if (window.__ddjBeforeAndAfter) return;
  window.__ddjBeforeAndAfter = true;

  var SELECTOR = '[data-ba]';

  function init(root) {
    if (root.dataset.baReady === 'true') return;

    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-ba-tab]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-ba-panel]'));
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-ba-card]'));
    if (panels.length < 2) return;

    root.dataset.baReady = 'true';

    var tablist = root.querySelector('[data-ba-tablist]');
    var prev = root.querySelector('[data-ba-prev]');
    var next = root.querySelector('[data-ba-next]');
    var current = 0;

    /* Grouped mode (the clinic page): a tab is a treatment CATEGORY holding
     * several cases, not one case. Each tab names its first case in
     * data-ba-first and each panel names its tab in data-ba-group. The
     * chevrons move TAB to tab, showing each category's first case, so every
     * click moves the underline with the pictures. Stepping case by case left
     * the underline still while the pictures changed, which read as broken
     * (Tim, 30 Sep: "the active state on the tabs doesn't [change]. It's
     * buggy"). The other cases in a category are not reachable here; they
     * live on the category's own page. */
    var grouped = tabs.length > 0 && tabs[0].hasAttribute('data-ba-first');
    function firstOf(tabIndex) {
      return grouped ? Number(tabs[tabIndex].getAttribute('data-ba-first')) : tabIndex;
    }
    function tabOf(panelIndex) {
      return grouped ? Number(panels[panelIndex].getAttribute('data-ba-group')) : panelIndex;
    }

    /* The strip is a scroller on mobile and can overflow on desktop too, so the
     * tab that just became active is brought into view rather than left off the
     * end after a chevron press. The STRIP scrolls, never the page:
     * `scrollIntoView` also scrolls every ancestor, the window included, and on
     * load that dragged the page down to the section before anyone touched it. */
    function reveal(tab) {
      if (!tablist || !tab) return;
      if (tablist.scrollWidth <= tablist.clientWidth) return;
      var left = tab.getBoundingClientRect().left - tablist.getBoundingClientRect().left + tablist.scrollLeft;
      var right = left + tab.offsetWidth;
      var target = null;
      if (left < tablist.scrollLeft) target = left;
      else if (right > tablist.scrollLeft + tablist.clientWidth) target = right - tablist.clientWidth;
      if (target === null) return;
      tablist.scrollTo({ left: target, behavior: 'smooth' });
    }

    function show(index, focusTab, initial) {
      if (index < 0 || index >= panels.length) return;
      current = index;

      panels.forEach(function (panel, i) {
        panel.hidden = i !== index;
      });

      /* A case with a treatment has its own card and stat row, rendered twice
       * (mobile slot and desktop column), so they are matched by the case index
       * they carry rather than by position. */
      cards.forEach(function (card) {
        card.hidden = Number(card.getAttribute('data-ba-card')) !== index;
      });

      var activeTab = tabOf(index);
      tabs.forEach(function (tab, i) {
        var on = i === activeTab;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });

      /* Ends are disabled rather than wrapped: the strip reads as a list with a
       * first and a last, and a silent jump back to the start from the end
       * would contradict that. */
      if (grouped) {
        if (prev) prev.disabled = activeTab === 0;
        if (next) next.disabled = activeTab === tabs.length - 1;
      } else {
        if (prev) prev.disabled = index === 0;
        if (next) next.disabled = index === panels.length - 1;
      }

      if (tabs[activeTab]) {
        if (!initial) reveal(tabs[activeTab]);
        if (focusTab) tabs[activeTab].focus();
      }
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        show(firstOf(i));
      });
    });

    /* Arrow keys move between tabs, which is what a tablist is expected to do
     * once one of its tabs has focus. */
    if (tablist) {
      tablist.addEventListener('keydown', function (event) {
        /* Keys move between TABS, which in grouped mode are not the cases. */
        var count = grouped ? tabs.length : panels.length;
        var at = tabOf(current);
        var delta = 0;
        if (event.key === 'ArrowRight') delta = 1;
        else if (event.key === 'ArrowLeft') delta = -1;
        else if (event.key === 'Home') delta = -at;
        else if (event.key === 'End') delta = count - 1 - at;
        else return;

        var target = at + delta;
        if (target < 0 || target >= count) return;
        event.preventDefault();
        show(firstOf(target), true);
      });
    }

    function step(delta) {
      if (grouped) {
        var target = tabOf(current) + delta;
        if (target >= 0 && target < tabs.length) show(firstOf(target));
      } else {
        show(current + delta);
      }
    }

    if (prev) {
      prev.addEventListener('click', function () { step(-1); });
    }

    if (next) {
      next.addEventListener('click', function () { step(1); });
    }

    show(0, false, true);
  }

  function initAll(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll(SELECTOR), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  /* Theme editor: a re-rendered section arrives with fresh, unbound tabs. */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
